import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { useAuth, useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";
import { DeletePlayerDialog, PasswordDialog } from "layouts/players/PlayerActions";
import { CreateAccountDialog, CreatedAccountAlert } from "layouts/players/CreateAccount";

function RowAction({ title, icon, color, disabled, onClick }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          color={color}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
        >
          <Icon>{icon}</Icon>
        </IconButton>
      </span>
    </Tooltip>
  );
}

RowAction.defaultProps = { disabled: false };

RowAction.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

function Players() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const online = params.get("online") === "1";
  const page = Number(params.get("page")) || 1;
  const size = Number(params.get("size")) || 20;
  const [input, setInput] = useState(q);
  const [dialog, setDialog] = useState(null);
  const canAccount = useCan(PERMISSIONS.PLAYER_ACCOUNT);
  const isOwner = !!useAuth().admin?.isOwner;
  const [created, setCreated] = useState(null);

  const update = (patch) => {
    const next = { q, online: online ? "1" : "", page: String(page), size: String(size), ...patch };
    setParams(
      Object.fromEntries(Object.entries(next).filter(([, v]) => v !== "" && v !== undefined))
    );
  };

  const { data, loading, error, reload } = useFetch(
    () => api.get("/api/admin/players", { q, online, page, size }),
    [q, online, page, size]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="players" />
        <PageCard
          title="Người chơi"
          subtitle={data ? `${formatNumber(data.total)} nhân vật` : null}
          noPadding
          actions={
            isOwner && (
              <MDButton
                size="small"
                variant="gradient"
                color="success"
                onClick={() => setDialog({ kind: "create" })}
              >
                <Icon>person_add</Icon>&nbsp;Tạo tài khoản
              </MDButton>
            )
          }
        >
          {created && (
            <MDBox px={2}>
              <CreatedAccountAlert account={created} onClose={() => setCreated(null)} />
            </MDBox>
          )}
          <MDBox
            px={2}
            pb={1}
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              update({ q: input.trim(), page: "1" });
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDInput
                  fullWidth
                  label="Tên nhân vật, tên tài khoản hoặc user id"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                />
              </Grid>
              <Grid item xs="auto">
                <MDButton type="submit" variant="gradient" color="info">
                  Tìm
                </MDButton>
              </Grid>
              <Grid item xs="auto">
                <FormControlLabel
                  control={
                    <Switch
                      checked={online}
                      onChange={(event) =>
                        update({ online: event.target.checked ? "1" : "", page: "1" })
                      }
                    />
                  }
                  label="Chỉ người đang online"
                />
              </Grid>
            </Grid>
            {error && <Chip color="error" label={error} sx={{ mt: 1 }} />}
          </MDBox>
          <AdminTable
            loading={loading}
            rows={data?.items || []}
            rowKey="userId"
            page={page}
            size={size}
            total={data?.total ?? 0}
            onPageChange={(p) => update({ page: String(p) })}
            onSizeChange={(s) => update({ size: String(s), page: "1" })}
            onRowClick={(row) => navigate(`/players/${row.userId}`)}
            columns={[
              { key: "userId", label: "User ID", width: 80 },
              {
                key: "name",
                label: "Nhân vật",
                render: (p) => (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    {p.name}
                    {p.isAdmin && <Chip size="small" color="warning" label="Admin" />}
                    {p.banned && <Chip size="small" color="error" label="Bị khoá" />}
                  </MDBox>
                ),
              },
              { key: "username", label: "Tài khoản" },
              { key: "gold", label: "Vàng", align: "right", render: (p) => formatNumber(p.gold) },
              { key: "coin", label: "Ngọc", align: "right", render: (p) => formatNumber(p.coin) },
              { key: "lua", label: "Lúa", align: "right", render: (p) => formatNumber(p.lua) },
              {
                key: "star",
                label: "Năng lượng",
                align: "right",
                render: (p) => formatNumber(p.star),
              },
              { key: "vnd", label: "Pet Coin", align: "right", render: (p) => formatNumber(p.vnd) },
              {
                key: "online",
                label: "Trạng thái",
                render: (p) =>
                  p.online ? (
                    <Chip size="small" color="success" label="Online" />
                  ) : (
                    formatDate(p.lastOnline)
                  ),
              },
              canAccount && {
                key: "actions",
                label: "",
                align: "right",
                render: (p) => (
                  <MDBox display="flex" justifyContent="flex-end">
                    <RowAction
                      title="Đặt mật khẩu mới"
                      icon="key"
                      color="warning"
                      onClick={() => setDialog({ kind: "password", player: p })}
                    />
                    <RowAction
                      title={
                        p.online
                          ? "Đang online, kick trước khi xoá"
                          : p.isAdmin
                          ? "Không xoá được admin"
                          : "Xoá người chơi"
                      }
                      icon="delete"
                      color="error"
                      disabled={p.online || p.isAdmin}
                      onClick={() => setDialog({ kind: "delete", player: p })}
                    />
                  </MDBox>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </MDBox>
      <Footer />
      {dialog?.kind === "password" && (
        <PasswordDialog
          userId={dialog.player.userId}
          username={dialog.player.username || `#${dialog.player.userId}`}
          onClose={() => setDialog(null)}
          onDone={() => reload(true)}
        />
      )}
      {dialog?.kind === "create" && (
        <CreateAccountDialog
          onClose={() => setDialog(null)}
          onCreated={(account) => {
            setCreated(account);
            reload(true);
          }}
        />
      )}
      {dialog?.kind === "delete" && (
        <DeletePlayerDialog
          userId={dialog.player.userId}
          name={dialog.player.name}
          username={dialog.player.username || `#${dialog.player.userId}`}
          onClose={() => setDialog(null)}
          onDone={() => reload(true)}
        />
      )}
    </DashboardLayout>
  );
}

export default Players;
